/**
 * Cómo se dibuja una persona que no dio su nombre.
 *
 * Desde 2026-09-09 un lead existe con **cualquiera** de sus tres
 * identificadores —nombre, teléfono o correo—, porque quien llama al número de
 * demostración puede no dar su nombre nunca y esa llamada tiene que quedar
 * registrada igual. El servidor manda `firstName` VACÍO cuando no lo sabe: no
 * inventa un nombre y ya no rellena el hueco con el id del documento, que era
 * peor —la persona se dibujaba como `4KfR2mZq8Nx1…` en todas las pantallas—.
 *
 * Este módulo existe para que ese hueco se vea IGUAL en todas partes. Antes
 * convivían cuatro textos distintos para el mismo hecho («Sin nombre», «Lead sin
 * nombre», «Contacto sin nombre» y «Contacto sin cargar», que además mentía:
 * decía que el dato no había cargado cuando sí cargó y estaba vacío).
 *
 * ES UNA CAPA DE DIBUJO, NO UN DATO. El placeholder no se guarda, no se manda al
 * servidor y no entra en una exportación: un CSV que dijera «Sin nombre» en la
 * columna Nombre estaría afirmando que la persona se llama así. Por eso esto no
 * vive dentro del modelo (`LeadRow.name` sigue siendo el nombre real, vacío
 * cuando no hay) sino acá, y se aplica en el componente que pinta.
 */

/** El texto, uno solo. Si hay que cambiarlo, se cambia acá. */
export const SIN_NOMBRE = 'Sin nombre';

/**
 * El nombre tal como se muestra: el real si lo hay, el placeholder si no.
 *
 * Usalo en el JSX, nunca al armar los datos.
 *
 * **El número.** Cuando en un tablero conviven varias fichas sin nombre,
 * llamarlas a todas igual las vuelve imposibles de nombrar en voz alta —«¿cuál
 * de las cinco?»—. El servidor le asigna a cada una un número al crearla
 * (`anonymousNumber`) y acá se dibuja: «Sin nombre #7». Es de la persona y no
 * se mueve: no es su posición en la lista, que cambiaría con cada alta.
 *
 * Sin número —las fichas creadas antes de que esto existiera— queda «Sin
 * nombre» a secas, que es lo correcto: inventarle un número la haría ver como
 * la #1 de un conteo que nadie llevó.
 */
export function nombreParaMostrar(
  nombre: string | null | undefined,
  numero?: number | null,
): string {
  const real = (nombre ?? '').trim();
  if (real) return real;
  return typeof numero === 'number' && Number.isInteger(numero) && numero > 0
    ? `${SIN_NOMBRE} #${numero}`
    : SIN_NOMBRE;
}

/**
 * `true` cuando la persona de verdad no tiene nombre.
 *
 * Sirve para decidir un estilo —el placeholder va en gris, atenuado, para que se
 * lea como un hueco y no como el nombre de alguien— y para contar cuántas fichas
 * están así. No lo uses para decidir si dibujar la fila: la fila se dibuja
 * siempre.
 */
export function esSinNombre(nombre: string | null | undefined): boolean {
  return !(nombre ?? '').trim();
}

/**
 * La semilla del color del avatar.
 *
 * `hashBucket('')` es una constante, así que sin esto TODAS las personas sin
 * nombre comparten color: doce filas con el mismo `?` del mismo tono son
 * indistinguibles de un vistazo. Con el id de la ficha cada una recibe el suyo y
 * sigue siendo estable entre sesiones, que es la propiedad que se le pide al
 * color.
 */
export function semillaDeAvatar(
  nombre: string | null | undefined,
  id: string | null | undefined,
): string {
  return (nombre ?? '').trim() || (id ?? '').trim();
}
